/**
 * Public web routes that must render without waiting for authentication.
 *
 * Why this exists: the root layout holds every route back until Supabase auth resolves, so
 * that the native splash is not replaced by an empty view on cold start. During Expo's
 * static web export there is no auth to resolve, so that gate made every exported page an
 * empty shell — no text, no title, nothing for a crawler or an ad-network reviewer to read.
 *
 * Routes listed here opt out of that gate on web. They must be routes that render the same
 * output signed in or signed out, because the static render and the first client render have
 * to agree or React will report a hydration mismatch. Authenticated routes (`/app/*`) are
 * deliberately absent.
 */

export const PUBLIC_STATIC_ROUTES = [
  '/',
  '/terms',
  '/privacy',
  '/data-deletion',
  '/credits',
  '/about',
  '/support',
  '/contact',
] as const

const PUBLIC_STATIC_ROUTE_SET = new Set<string>(PUBLIC_STATIC_ROUTES)

/**
 * True when the path is a public route that renders without auth.
 *
 * Must stay a pure function of `pathname` — reading auth state, `typeof window`, or any
 * other runtime-only value here would make the server and client renders disagree.
 * A trailing slash is tolerated; Expo Router emits '' for the site root.
 */
export function isPublicStaticRoute(pathname: string | null | undefined): boolean {
  if (pathname === null || pathname === undefined) {
    return false
  }
  if (pathname === '') {
    return true
  }
  const normalized =
    pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
  return PUBLIC_STATIC_ROUTE_SET.has(normalized)
}
